<template>
  <v-container>
    <v-form
      v-model="isFormValid"
      lazy-validation
      ref="form"
      @submit.prevent="mergePerson"
    >
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="person.firstName"
            label="First Name"
            :rules="nameRules"
            :hint="person.personId ? `🆔 ${person.personId}` : ''"
            persistent-hint
            required
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="person.lastName"
            label="Last Name"
            :rules="nameRules"
            required
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="person.email"
            label="Email"
            :rules="emailRules"
            required
          >
            <!-- ✅ ADD: Clickable email icon -->
            <template v-slot:append>
              <v-icon
                @click="sendEmail(person.email)"
                :disabled="!person.email"
                color="primary"
                style="cursor: pointer;"
                title="Send email"
              >
                email
              </v-icon>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="person.phone"
            label="Phone"
            :rules="phoneRules"
            required
          >
            <!-- ✅ ADD: Clickable phone icon -->
            <template v-slot:append>
              <v-icon
                @click="callPhone(person.phone)"
                :disabled="!person.phone"
                color="primary"
                style="cursor: pointer;"
                title="Call this number"
              >
                phone
              </v-icon>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-menu
            v-model="birthDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="person.birthDate"
                label="Birth Date"
                prepend-inner-icon="event"
                v-on="on"
                v-bind="attrs"
                required
              />
            </template>
            <v-date-picker
              v-model="person.birthDate"
              @input="birthDateMenu = false"
              required
            />
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="person.gender"
            :items="['male', 'female', 'Other']"
            label="Gender"
            required
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="person.photo" label="Photo URL" />
        </v-col>
        <v-col cols="12" sm="6">
          <catValueDropDown
            :outlined="true"
            v-model="person.sourceOfInformation"
            label="Lead From"
            catName="CLUB_LEADS"
            :multiList="false"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field v-model="person.homeAddress" label="Home Address" />
        </v-col>
        <v-col cols="12" sm="6">
          <catValueDropDown
            :outlined="true"
            v-model="person.country"
            label="Country"
            catName="COUNTRY_LIST"
            :multiList="false"
          />
        </v-col>

        <v-col cols="12" sm="6">
          <v-text-field v-model="person.postalCode" label="Postal Code" />
        </v-col>

        <v-col cols="12" sm="6">
          <v-select
            v-model="person.registrationState"
            :items="catValueListArray('person_registration_state')"
            label="Registration State"
            required
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="person.createDate"
            label="Create Date"
            prepend-inner-icon="event"
            :disabled="true"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="text-right">
          <v-btn color="primary" :disabled="!isFormValid" @click="savePerson">
            Save</v-btn
          >
        </v-col>
      </v-row>
    </v-form>
    <v-snackbar v-model="showToast" color="success" timeout="3000">
      {{ toastMsg }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import catValueDropDown from "../../shared/cat_val_dropdown.vue";

export default {
  components: {
    catValueDropDown,
  },
  props: [
    "editedItem",
    "nameRules",
    "emailRules",
    "phoneRules",
    "catValueListArray",
  ],
  data() {
    return {
      isFormValid: false,
      birthDateMenu: false,
      createDateMenu: false,
      person: {},
      showToast: false,
      toastMsg: "",
    };
  },

  created() {
    if (this.editedItem) {
      this.person = { ...this.editedItem };
    } else {
      this.person = {
        personId: 0,
        firstName: "",
        lastName: "",
        email: "",
        birthDate: "",
        registrationState: "",
        photo: "",
        gender: "",
        country: "",
        createDate: "",
        updateSource: "",
        phone: "",
        sourceOfInformation: "",
        homeAddress: "",
        postalCode: "",
      };
    }
  },
  computed: {
    ...mapGetters(["user"]),
  },
  watch: {
    editedItem: {
      handler(newVal) {
        if (newVal) {
          this.person = { ...newVal };
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions(["personMerge"]),
    getPersonPayload(person) {
      return {
        personId: person.personId,
        firstName: person.firstName,
        lastName: person.lastName,
        birthDate: person.birthDate,
        photo: person.photo,
        gender: person.gender,
        email: person.email,
        language: person.language,
        country: person.country,
        createDate: person.createDate,
        updateDate: person.updateDate,
        updateSource: person.updateSource,
        phone: person.phone,
        sourceOfInformation: person.sourceOfInformation,
        homeAddress: person.homeAddress,
        postalCode: person.postalCode,
        occupation: person.occupation,
        otherInfo: person.otherInfo,
        registrationState: person.registrationState,
      };
    },
    savePerson() {
      this.person.updateSource = this.user.userName;
      const personPayload = this.getPersonPayload(this.person);
      this.personMerge(personPayload).then(() => {
        this.toastMsg = "Person saved successfully!";
        this.showToast = true;
        this.dialog = false;
      });
    },
    // ✅ ADD: Simple email method
    sendEmail(email) {
      if (!email) return;
      
      // Create mailto: link to open email client
      window.open(`mailto:${email}`, '_self');
    },

    // ✅ EXISTING: callPhone method
    callPhone(phoneNumber) {
      if (!phoneNumber) return;

      // Clean the phone number (remove spaces, dashes, etc.)
      const cleanPhone = phoneNumber.replace(/[^\d+]/g, "");

      // Create tel: link to trigger phone call
      window.open(`tel:${cleanPhone}`, "_self");
    },
  },
};
</script>